"use client";

import { useEffect, useId, useRef, useState, type ReactNode } from "react";

/**
 * Nav container shared by the marketing pill-nav (`SiteNav`) and the in-app
 * top-bar (`AppNav`). The primary links are rendered ONCE (as `children`) and
 * drive both layouts:
 *   - Desktop: an inline row, laid out exactly as before (the wrapper is a flex
 *     row that inherits the nav's spacing).
 *   - Phones (<=640px): the brand + links + CTA can't share one row, so the row
 *     stranded the brand and wrapped (the bug). Here the links instead collapse
 *     behind a hamburger toggle into a dropdown panel; `trailing` (the account
 *     menu, when signed in) stays visible in the bar beside the toggle.
 *
 * Open/close mirrors AccountMenu exactly: local open state + outside-click +
 * Escape, with `aria-haspopup`/`aria-expanded`/`aria-controls` on the toggle.
 */
export function NavShell({
  className,
  ariaLabel = "Primary",
  trailing,
  children,
}: {
  className: string;
  ariaLabel?: string;
  trailing?: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // NOTE: render `className` alone (no extra marker class) — the chrome tests
  // assert the nav is exactly `class="pz-sitenav"` / `class="pz-appnav"`. The
  // mobile dropdown anchors on those same classes (position: relative in CSS).
  return (
    <nav className={className} aria-label={ariaLabel} ref={ref}>
      {/* Collapsible primary links. Tapping any link closes the panel (mobile);
          harmless on desktop, where the panel is a plain always-visible row. */}
      <div id={menuId} className="pz-navmenu" data-open={open || undefined} onClick={() => setOpen(false)}>
        {children}
      </div>
      <button
        type="button"
        className="pz-navtoggle"
        aria-label="Menu"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
      >
        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
          {open ? (
            <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          ) : (
            <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          )}
        </svg>
      </button>
      {trailing}
    </nav>
  );
}
